import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useSalon } from '../../contexts/SalonContext';
import { collection, getDocs, query, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { addUserToSalon, removeUserFromSalon, updateUserPermissions } from '../../firebase/userRoles';
import { registerWithEmail } from '../../firebase/auth';

/**
 * Componente para gerenciar usuários do salão
 */
const UserManagement = () => {
  const { selectedSalonId, hasPermission, refreshUserData } = useUser();
  const { salonConfig } = useSalon();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'professional',
    password: '',
    permissions: []
  });
  const [editingUser, setEditingUser] = useState(null);
  
  // Lista de permissões disponíveis para gerentes
  const managerPermissions = [
    { id: 'manage_appointments', label: 'Gerenciar Agendamentos' },
    { id: 'manage_professionals', label: 'Gerenciar Profissionais' },
    { id: 'manage_services', label: 'Gerenciar Serviços' },
    { id: 'manage_clients', label: 'Gerenciar Clientes' },
    { id: 'view_reports', label: 'Visualizar Relatórios' },
    { id: 'manage_salon_settings', label: 'Configurações do Salão' }
  ];
  
  // Carregar usuários do salão
  const loadUsers = useCallback(async () => {
    if (!selectedSalonId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Obter documento do salão
      const salonDoc = await getDoc(doc(db, 'salons', selectedSalonId));
      if (!salonDoc.exists()) {
        throw new Error('Salão não encontrado');
      }
      
      const salonData = salonDoc.data();
      const userIds = [
        ...(salonData.owners || []),
        ...(salonData.managers || []),
        ...(salonData.admins || [])
      ];
      
      if (userIds.length === 0) {
        setUsers([]);
        return;
      }
      
      // Buscar informações de cada usuário
      const usersData = await Promise.all(
        userIds.map(async (userId) => {
          // Buscar dados do usuário
          const userDoc = await getDoc(doc(db, 'users', userId));
          
          // Buscar roles do usuário
          const userRolesDoc = await getDoc(doc(db, 'userRoles', userId));
          let role = 'unknown';
          let permissions = [];
          
          if (userRolesDoc.exists()) {
            const userData = userRolesDoc.data();
            const salonRole = userData.salons.find(s => s.salonId === selectedSalonId);
            
            if (salonRole) {
              role = salonRole.role;
              permissions = salonRole.permissions || [];
              
              // Encontrar profissional associado se for profissional
              if (role === 'professional' && salonRole.professionalId) {
                const profDoc = await getDoc(
                  doc(db, `salons/${selectedSalonId}/professionals`, salonRole.professionalId)
                );
                
                if (profDoc.exists()) {
                  return {
                    id: userId,
                    ...userDoc.data(),
                    role,
                    permissions,
                    professionalId: salonRole.professionalId,
                    professionalInfo: profDoc.data()
                  };
                }
              }
            }
          }
          
          return {
            id: userId,
            ...userDoc.data(),
            role,
            permissions
          };
        })
      );
      
      setUsers(usersData);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Não foi possível carregar os usuários. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedSalonId]);
  
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);
  
  // Verificar permissão para gerenciar usuários
  const [canManageUsers, setCanManageUsers] = useState(false);
  
  useEffect(() => {
    const checkPermission = async () => {
      const hasManagePermission = await hasPermission('manage_salon_settings');
      setCanManageUsers(hasManagePermission);
    };
    
    checkPermission();
  }, [hasPermission]);
  
  // Adicionar um novo usuário
  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!canManageUsers) {
      setError('Você não tem permissão para adicionar usuários');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Registrar usuário no Firebase Auth
      const result = await registerWithEmail(newUser.email, newUser.password);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao cadastrar usuário');
      }
      
      const userId = result.user.uid;
      
      // Determinar permissões baseadas no papel
      let permissions = [];
      if (newUser.role === 'manager') {
        permissions = newUser.permissions;
      }
      
      // Adicionar usuário ao salão com o papel selecionado
      await addUserToSalon(userId, selectedSalonId, newUser.role, permissions);
      
      // Atualizar lista de usuários
      await loadUsers();
      
      // Limpar formulário
      setNewUser({
        email: '',
        name: '',
        role: 'professional',
        password: '',
        permissions: []
      });
      
      setShowAddUser(false);
    } catch (err) {
      console.error('Erro ao adicionar usuário:', err);
      setError(err.message || 'Erro ao adicionar usuário');
    } finally {
      setLoading(false);
    }
  };
  
  // Atualizar permissões de um usuário
  const handleUpdatePermissions = async () => {
    if (!editingUser || !canManageUsers) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await updateUserPermissions(
        editingUser.id,
        selectedSalonId,
        editingUser.permissions
      );
      
      // Atualizar lista de usuários
      await loadUsers();
      
      // Limpar formulário de edição
      setEditingUser(null);
    } catch (err) {
      console.error('Erro ao atualizar permissões:', err);
      setError(err.message || 'Erro ao atualizar permissões');
    } finally {
      setLoading(false);
    }
  };
  
  // Remover um usuário do salão
  const handleRemoveUser = async (userId) => {
    if (!canManageUsers) {
      setError('Você não tem permissão para remover usuários');
      return;
    }
    
    if (!window.confirm('Tem certeza que deseja remover este usuário do salão?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await removeUserFromSalon(userId, selectedSalonId);
      
      // Atualizar lista de usuários
      await loadUsers();
      
      // Atualizar dados no contexto de usuário
      await refreshUserData();
    } catch (err) {
      console.error('Erro ao remover usuário:', err);
      setError(err.message || 'Erro ao remover usuário');
    } finally {
      setLoading(false);
    }
  };
  
  // Lidar com mudança de permissões no formulário de edição
  const handlePermissionChange = (permissionId) => {
    setEditingUser(prev => {
      if (!prev) return prev;
      
      const newPermissions = [...prev.permissions];
      const index = newPermissions.indexOf(permissionId);
      
      if (index === -1) {
        newPermissions.push(permissionId);
      } else {
        newPermissions.splice(index, 1);
      }
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };
  
  // Lidar com mudança de permissões para novo usuário
  const handleNewUserPermissionChange = (permissionId) => {
    setNewUser(prev => {
      const newPermissions = [...prev.permissions];
      const index = newPermissions.indexOf(permissionId);
      
      if (index === -1) {
        newPermissions.push(permissionId);
      } else {
        newPermissions.splice(index, 1);
      }
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };
  
  // Exibir texto de role em português
  const getRoleName = (role) => {
    const roles = {
      'owner': 'Proprietário',
      'admin': 'Administrador',
      'manager': 'Gestor',
      'professional': 'Profissional',
      'unknown': 'Desconhecido'
    };
    
    return roles[role] || role;
  };
  
  if (!selectedSalonId) {
    return <div className="p-4">Selecione um salão para gerenciar usuários</div>;
  }
  
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Gerenciamento de Usuários</h2>
        {canManageUsers && (
          <button
            onClick={() => setShowAddUser(!showAddUser)}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            {showAddUser ? 'Cancelar' : 'Adicionar Usuário'}
          </button>
        )}
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {/* Formulário para adicionar usuário */}
      {showAddUser && (
        <div className="mb-6 p-4 border rounded bg-gray-50">
          <h3 className="text-lg font-medium mb-4">Adicionar Novo Usuário</h3>
          <form onSubmit={handleAddUser}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  required
                  value={newUser.name}
                  onChange={e => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <input
                  type="password"
                  required
                  value={newUser.password}
                  onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Senha"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Usuário
                </label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="professional">Profissional</option>
                  <option value="manager">Gestor</option>
                  <option value="owner">Proprietário</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            
            {/* Permissões (apenas para gestores) */}
            {newUser.role === 'manager' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissões
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {managerPermissions.map(permission => (
                    <div key={permission.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`new-perm-${permission.id}`}
                        checked={newUser.permissions.includes(permission.id)}
                        onChange={() => handleNewUserPermissionChange(permission.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`new-perm-${permission.id}`} className="ml-2 text-sm text-gray-700">
                        {permission.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowAddUser(false)}
                className="mr-2 px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Adicionando...' : 'Adicionar Usuário'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Modal de edição de permissões */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              Editar Permissões - {editingUser.displayName || editingUser.email}
            </h3>
            
            <div className="mb-4">
              {managerPermissions.map(permission => (
                <div key={permission.id} className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id={`edit-perm-${permission.id}`}
                    checked={editingUser.permissions.includes(permission.id)}
                    onChange={() => handlePermissionChange(permission.id)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`edit-perm-${permission.id}`} className="ml-2 text-sm text-gray-700">
                    {permission.label}
                  </label>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setEditingUser(null)}
                className="mr-2 px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleUpdatePermissions}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : 'Salvar Permissões'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Lista de usuários */}
      {loading && users.length === 0 ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Carregando usuários...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nenhum usuário encontrado para este salão
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {canManageUsers && (
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.photoURL ? (
                          <img className="h-10 w-10 rounded-full" src={user.photoURL} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-700 font-medium">
                              {(user.displayName || user.email || '?')[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName || 'Sem nome'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${user.role === 'owner' ? 'bg-purple-100 text-purple-800' : 
                        user.role === 'admin' ? 'bg-blue-100 text-blue-800' : 
                          user.role === 'manager' ? 'bg-green-100 text-green-800' : 
                            'bg-yellow-100 text-yellow-800'}`}
                    >
                      {getRoleName(user.role)}
                    </span>
                    {user.role === 'professional' && user.professionalInfo && (
                      <div className="text-xs text-gray-500 mt-1">
                        {user.professionalInfo.nome || 'Profissional não configurado'}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Ativo
                    </span>
                  </td>
                  {canManageUsers && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {user.role === 'manager' && (
                        <button
                          onClick={() => setEditingUser(user)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Permissões
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remover
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 